// DominantArea component - right side with LLM, minimap, and settings
import type {
  DominantAreaConfig,
  DominantAreaAPI,
} from "../../../../../../types/stage-types.js";
import {
  createLLMControlsInContainer,
  type LLMControlsAPI,
} from "./subcomponents/LLMControls/index.js";
import {
  createSettingsControlsInContainer,
  type SettingsControlsAPI,
} from "./subcomponents/SettingsControls/index.js";
import type { DominantVisibilityConfig } from "@/components/Stage/subcomponents/UIControlsLayer/visibility-profiles";
import type { HandednessStateAPI } from "@/state/handedness";

/**
 * Creates the DominantArea component with LLM interface, minimap, and settings
 * @param container - Container element for the DominantArea
 * @param config - Configuration options
 * @returns DominantArea API
 */
export const createDominantArea = (
  container: HTMLElement,
  config: DominantAreaConfig = {}
): DominantAreaAPI => {
  let isDestroyed = false;
  let llmControlsAPI: LLMControlsAPI | null = null;
  let settingsControlsAPI: SettingsControlsAPI | null = null;
  let unsubscribeUIStateChanged: (() => void) | null = null;
  let unsubscribeStore: (() => void) | null = null;
  let unsubscribeSessionUpdated: (() => void) | null = null;
  let unsubscribeProjectChanged: (() => void) | null = null;
  let llmContainerEl: HTMLElement | null = null;
  let minimapContainerEl: HTMLElement | null = null;
  let settingsContainerEl: HTMLElement | null = null;
  let rootElement: HTMLElement | null = null;
  let unsubscribeHandedness: (() => void) | null = null;

  const handednessState: HandednessStateAPI | null =
    (config.handedness as HandednessStateAPI | null) ?? null;

  let currentHandedness: "left" | "right" =
    (handednessState?.get?.() as "left" | "right" | undefined) ?? "right";

  const applyHandednessClass = (value: "left" | "right") => {
    currentHandedness = value;
    if (!rootElement) return;
    rootElement.dataset.handedness = value;
    rootElement.classList.toggle("is-left-handed", value === "left");
    rootElement.classList.toggle("is-right-handed", value !== "left");
  };

  /**
   * Initialize DominantArea HTML structure
   */
  const initDominantArea = (): void => {
    container.innerHTML = `
      <div class="dominant">
        <!-- llm: 44px × 106px -->
        <div class="llm-container"></div>
        <!-- minimap: width:80px, height:flex-grow:1 -->
        <div class="minimap"></div>
        <!-- settings: 44px × 106px -->
        <div class="settings-container"></div>
      </div>
    `;

    rootElement = container.querySelector(".dominant") as HTMLElement;
    applyHandednessClass(currentHandedness);

    llmContainerEl = container.querySelector(".llm-container") as HTMLElement;
    minimapContainerEl = container.querySelector(".minimap") as HTMLElement;
    settingsContainerEl = container.querySelector(
      ".settings-container"
    ) as HTMLElement;

    // Initialize LLMControls component
    const llmContainer = container.querySelector(
      ".llm-container"
    ) as HTMLElement;
    if (llmContainer) {
      try {
        const uiState =
          (config as any)?.appStateManager?.uiState ||
          (createDominantArea as any).__appStateManager?.uiState ||
          (container as any).__appStateManager?.uiState ||
          null;
        const store = (config as any)?.appStateManager?.store || null;
        const getScreenPath =
          (config as any)?.appStateManager?.getScreenStatePath || (() => null);
        const currentMuted = uiState?.getState?.().audio_muted ?? false;
        llmControlsAPI = createLLMControlsInContainer(llmContainer, {
          onAudioToggle: (isMuted) => {
            try {
              // Write via store if available; UI state adapter will mirror
              const p = getScreenPath("audioMuted");
              if (store && p) {
                store.set(p, isMuted);
              } else {
                uiState?.setAudioMuted?.(isMuted);
              }
            } catch {}
          },
          onLLMClick: () => console.log("LLM clicked"),
          initialMuted: currentMuted,
        });

        // Listen to store or UI state to keep control in sync
        try {
          const eventBus = (config as any)?.appStateManager?.appContext
            ?.eventBus;
          if (eventBus) {
            unsubscribeUIStateChanged = eventBus.on(
              "ui-state-changed",
              (data: any) => {
                try {
                  const muted = data?.state?.audio_muted;
                  if (typeof muted === "boolean") {
                    llmControlsAPI?.setMuted(muted);
                  }
                } catch {}
              }
            );
          }
          if (store) {
            const subscribeToAudioMuted = () => {
              // Clean up previous subscription if any
              if (unsubscribeStore) {
                try {
                  unsubscribeStore();
                } catch {}
                unsubscribeStore = null;
              }

              const p = getScreenPath("audioMuted");
              if (p) {
                // Exact path subscription when project id is known
                unsubscribeStore = store.subscribe(
                  p,
                  ({ value }: any) => {
                    if (typeof value === "boolean") {
                      llmControlsAPI?.setMuted(!!value);
                    }
                  },
                  { debounceMs: 0 }
                );
              } else {
                // Fallback: subscribe to any audioMuted path so we still react during early boot
                unsubscribeStore = store.subscribe(
                  /\.screenState\.audioMuted$/,
                  ({ value }: any) => {
                    if (typeof value === "boolean") {
                      llmControlsAPI?.setMuted(!!value);
                    }
                  },
                  { debounceMs: 0 }
                );
              }
            };

            // Initial subscription
            subscribeToAudioMuted();

            // Re-subscribe on project changes so we bind to the exact path once available
            if (eventBus) {
              unsubscribeProjectChanged = eventBus.on("project-changed", () =>
                subscribeToAudioMuted()
              );
            }
          }

          // Also reflect session updates directly (defensive)
          if (eventBus) {
            const prevUnsub = unsubscribeSessionUpdated;
            unsubscribeSessionUpdated = eventBus.on(
              "session:updated",
              (payload: any) => {
                try {
                  const screen = payload?.session?.currentProject?.screenState;
                  if (
                    screen &&
                    typeof (screen as any).audioMuted === "boolean"
                  ) {
                    llmControlsAPI?.setMuted(!!(screen as any).audioMuted);
                  }
                } catch {}
              }
            );
            // Ensure we don't leak multiple listeners if this block runs twice
            if (prevUnsub && prevUnsub !== unsubscribeSessionUpdated) {
              try {
                prevUnsub();
              } catch {}
            }
          }
        } catch {}
      } catch (error) {
        console.error("Failed to create LLMControls component:", error);
      }
    }

    // Initialize SettingsControls component
    const settingsContainer = container.querySelector(
      ".settings-container"
    ) as HTMLElement;
    if (settingsContainer) {
      try {
        settingsControlsAPI = createSettingsControlsInContainer(
          settingsContainer,
          {
            onSettingsClick: () => console.log("Settings clicked"),
            onPageSettingsClick: () => console.log("Page Settings clicked"),
            onAvatarClick: () => console.log("Avatar clicked"),
            userInitial: "U", // Default to "U" for User
            // Provide session so Avatar can pick avatarUrl/email/name
            session:
              (
                (config as any)?.appStateManager as any
              )?.appContext?.session?.getSession?.() || null,
          }
        );

        // Live session updates -> refresh avatar fields (forcing gravatar test: omit avatarUrl)
        try {
          const eventBus = (config as any)?.appStateManager?.appContext
            ?.eventBus;
          if (eventBus) {
            unsubscribeSessionUpdated = eventBus.on(
              "session:updated",
              (payload: any) => {
                const sess = payload?.session || null;
                const user = (sess as any)?.user || {};
                settingsControlsAPI?.setUser?.({
                  name: user?.name,
                  email: user?.email,
                  avatarUrl: user?.avatarUrl,
                });
              }
            );
          }
        } catch {}
      } catch (error) {
        console.error("Failed to create SettingsControls component:", error);
      }
    }
  };

  /**
   * Set component visibility
   * @param visible - Visibility state
   */
  const applyVisibility = (visibility: DominantVisibilityConfig): void => {
    if (isDestroyed) return;

    const dominantVisible = visibility.visible !== false;
    container.style.display = dominantVisible ? "" : "none";

    if (llmContainerEl) {
      const showLLM = visibility.llmAudio || visibility.llmButton;
      llmContainerEl.style.display = showLLM ? "" : "none";
    }
    llmControlsAPI?.applyVisibility({
      audio: visibility.llmAudio,
      llmButton: visibility.llmButton,
    });

    if (minimapContainerEl) {
      minimapContainerEl.style.display = visibility.map.visible ? "" : "none";
      if (visibility.map.variant) {
        minimapContainerEl.dataset.variant = visibility.map.variant;
      } else {
        delete minimapContainerEl.dataset.variant;
      }
    }

    settingsControlsAPI?.applyVisibility(visibility.settings);
    if (settingsContainerEl) {
      const hasSettingsVisible =
        (visibility.settings.page && visibility.settings.page.visible !== false) ||
        (visibility.settings.sketch &&
          visibility.settings.sketch.visible !== false) ||
        visibility.settings.project.visible !== false ||
        visibility.settings.user.visible !== false;
      settingsContainerEl.style.display = hasSettingsVisible ? "" : "none";
    }
  };

  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    container.style.visibility = visible ? "visible" : "hidden";
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Clean up components
    llmControlsAPI?.destroy();
    llmControlsAPI = null;

    settingsControlsAPI?.destroy();
    settingsControlsAPI = null;

    // Unsubscribe listeners
    if (unsubscribeUIStateChanged) {
      try {
        unsubscribeUIStateChanged();
      } catch {}
      unsubscribeUIStateChanged = null;
    }
    if (unsubscribeStore) {
      try {
        unsubscribeStore();
      } catch {}
      unsubscribeStore = null;
    }
    if (unsubscribeSessionUpdated) {
      try {
        unsubscribeSessionUpdated();
      } catch {}
      unsubscribeSessionUpdated = null;
    }
    if (unsubscribeProjectChanged) {
      try {
        unsubscribeProjectChanged();
      } catch {}
      unsubscribeProjectChanged = null;
    }
    if (unsubscribeHandedness) {
      try {
        unsubscribeHandedness();
      } catch {}
      unsubscribeHandedness = null;
    }

    rootElement = null;

    container.innerHTML = "";
  };

  // Initialize component
  initDominantArea();
  if (handednessState && typeof handednessState.subscribe === "function") {
    unsubscribeHandedness = handednessState.subscribe((value) => {
      if (value === "left" || value === "right") {
        applyHandednessClass(value);
      }
    });
  }

  // Return API
  return {
    setVisible,
    applyVisibility,
    destroy,
  };
};
